import { type FC, useCallback, useMemo, useState } from "react";

import { AutoSizer, VirtualTable, type VirtualTableSortEvent } from "@snapchat/snap-design-system";

import { useTableFilterAndSort } from "../../shared/hooks/useTableFilterAndSort";
import { type AssetResponse } from "../types/assets";

import { AssetsCard } from "./AssetsList.styles";
import { getColumns } from "./AssetsListColumns";
import { NoAssetsMessage } from "./messages.generated";

interface AssetsTableProps {
    assets: AssetResponse[];
    loading: boolean;
    total?: number;
}

export const AssetsTable: FC<AssetsTableProps> = ({ assets, loading, total }) => {
    const [searchText, setSearchText] = useState("");

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- any is aliased by SDS
    const columns = useMemo(() => getColumns(), []);

    const getSearchKey = useCallback((asset: AssetResponse) => asset.name, []);

    // Filtering comes from the shared hook; sorting is derived locally with
    // useMemo so it survives refetches (the hook stores sorted arrays in state
    // and resets them whenever the input data changes).
    const { data } = useTableFilterAndSort({
        tableData: assets,
        searchText,
        getSearchKey,
    });

    const [sort, setSort] = useState<VirtualTableSortEvent>({ columnId: "lastUpdated", sortOrder: "desc" });

    const onColumnSort = useCallback((event: VirtualTableSortEvent) => {
        setSort(event);
    }, []);

    const sortedData = useMemo(() => {
        const { columnId, sortOrder } = sort;
        if (!columnId || sortOrder === "none") {
            return [...data];
        }
        const direction = sortOrder === "desc" ? 1 : -1;
        return [...data].sort((a, b) => {
            const rawA = (a as Record<string, unknown>)[columnId];
            const rawB = (b as Record<string, unknown>)[columnId];
            // Rows without a value sort last regardless of direction.
            const missingA = rawA === null || rawA === undefined;
            const missingB = rawB === null || rawB === undefined;
            if (missingA && missingB) return 0;
            if (missingA) return 1;
            if (missingB) return -1;
            const compareA = String(rawA).toLowerCase();
            const compareB = String(rawB).toLowerCase();
            if (compareA === compareB) return 0;
            return compareA > compareB ? -direction : direction;
        });
    }, [data, sort]);

    const loadMoreData = useCallback((): Promise<unknown> => new Promise(() => null), []);

    return (
        <AssetsCard
            searchPlaceholder="Search for asset"
            padBody={false}
            onSearch={(text) => {
                setSearchText(text);
            }}
        >
            <AutoSizer>
                {({ width, height }: { width: number; height: number }) => {
                    return (
                        <VirtualTable
                            isLoading={loading || total === undefined}
                            columns={columns}
                            height={height}
                            width={width}
                            rowHeight={60}
                            emptyMessage={<NoAssetsMessage />}
                            data={sortedData}
                            loadMore={loadMoreData}
                            hasMore={total === undefined}
                            initialSortColumnId="lastUpdated"
                            initialSortOrder="desc"
                            onColumnSort={onColumnSort}
                        />
                    );
                }}
            </AutoSizer>
        </AssetsCard>
    );
};
